import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import {
  Box,
  Drawer,
  Stack,
  Switch,
  Theme,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import { Button, Divider, Settings01Icon, XCloseIcon } from "components";
import { GlobalTheme } from "configs";
import { gray, base } from "configs/colors";
import { produce } from "immer";
import debounce from "lodash/debounce";
import merge from "lodash/merge";
import { showTranslations } from "translation-check";
import { i18n } from "translations";
import { lineHeight, rem } from "utils";

import { CloseButton, SettingButton } from "./styles";

interface Props {
  children: ReactNode;
  showModeTool?: boolean;
}

interface AppThemeState {
  sidebar: {
    expandedHover: boolean;
    collapsed: boolean;
  };
  mode: "dark" | "light";
  toggleSidebar?: (v?: boolean) => void;
  expandSidebarHover?: (v: boolean) => void;
}

export enum ActionTypes {
  SIDEBAR_TOGGLE,
  SIDEBAR_EXPAND_HOVER,
  SIDEBAR_TYPE,
  THEME_MODE,
}

type ActionType =
  | {
      type: ActionTypes.SIDEBAR_TOGGLE;
      payload?: boolean;
    }
  | {
      type: ActionTypes.SIDEBAR_EXPAND_HOVER;
      payload: boolean;
    }
  | {
      type: ActionTypes.THEME_MODE;
      payload: AppThemeState["mode"];
    };

const initialState: AppThemeState = {
  sidebar: {
    expandedHover: false,
    collapsed: false,
  },
  mode: "light",
};

const reducer = produce((draft: AppThemeState, action: ActionType) => {
  switch (action.type) {
    case ActionTypes.SIDEBAR_TOGGLE: {
      draft.sidebar.collapsed = action.payload ?? !draft.sidebar.collapsed;
      break;
    }
    case ActionTypes.SIDEBAR_EXPAND_HOVER: {
      draft.sidebar.expandedHover = action.payload;
      break;
    }
    case ActionTypes.THEME_MODE: {
      draft.mode = action.payload;
      break;
    }
    default:
      break;
  }
});

export const AppThemeContext = React.createContext<AppThemeState>(initialState);

const AppThemeProvider: React.FC<Props> = ({ children, showModeTool }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(function windowResize() {
    const windowResizeHandler = debounce(() => {
      if (window.innerWidth > 1200 || state.sidebar.collapsed) return;
      dispatch({ type: ActionTypes.SIDEBAR_TOGGLE, payload: true });
    }, 300);
    windowResizeHandler();
    window.addEventListener("resize", windowResizeHandler);
    return () => {
      window.removeEventListener("resize", windowResizeHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleConfiguration = () => {
    setOpen((prev) => !prev);
  };

  const handleSidebarToggle = useCallback((v?: boolean) => {
    dispatch({ type: ActionTypes.SIDEBAR_TOGGLE, payload: v });
  }, []);

  const handleSidebarMiniChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    handleSidebarToggle(checked);
  };

  const handleSidebarExpandHover = useCallback((v: boolean) => {
    dispatch({ type: ActionTypes.SIDEBAR_EXPAND_HOVER, payload: v });
  }, []);

  const handleViewTranslations = () => {
    showTranslations(i18n);
  };

  const theme = useMemo<Theme>(() => {
    const isLightMode = state.mode === "light";
    return createTheme(
      merge(GlobalTheme, {
        palette: {
          mode: state.mode,
          divider: isLightMode ? gray[300] : "rgba(255, 255, 255, 0.12)",
          text: {
            primary: isLightMode ? gray[900] : base.White,
            secondary: isLightMode ? gray[500] : "rgba(255, 255, 255, 0.7)",
            disabled: isLightMode ? gray[300] : "rgba(255, 255, 255, 0.5)",
          },
        },
      })
    );
  }, [state.mode]);

  const value = useMemo(
    () => ({
      ...state,
      toggleSidebar: handleSidebarToggle,
      expandSidebarHover: handleSidebarExpandHover,
    }),
    [handleSidebarToggle, handleSidebarExpandHover, state]
  );

  return (
    <AppThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {children}
        {showModeTool && (
          <SettingButton onClick={toggleConfiguration} zIndex={999999}>
            <Settings01Icon fontSize="small" />
          </SettingButton>
        )}
        <Drawer open={open} anchor="right" onClose={toggleConfiguration}>
          <Stack paddingX="10px" gap="16px" minWidth="350px">
            <Box padding="32px 24px 4px" position="relative">
              <Typography
                variant="h5"
                fontSize={rem(20)}
                lineHeight={lineHeight(27.5)}
                fontWeight={700}
                letterSpacing={0}
              >
                Theme UI Configurator
              </Typography>
              <Typography variant="body1" fontWeight={300}>
                See our dashboard options.
              </Typography>
              <CloseButton size="small" onClick={toggleConfiguration}>
                <XCloseIcon fontSize="small" />
              </CloseButton>
            </Box>
            <Divider />
            <Stack padding="4px 24px 24px" gap="16px">
              <Stack
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="h6"
                  fontSize={rem(16)}
                  fontWeight={700}
                  letterSpacing={0}
                >
                  Sidenav Mini
                </Typography>
                <Switch
                  onChange={handleSidebarMiniChange}
                  checked={state.sidebar.collapsed}
                />
              </Stack>
              <Divider />
              <Stack
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="h6"
                  fontSize={rem(16)}
                  fontWeight={700}
                  letterSpacing={0}
                >
                  Translations
                </Typography>
                <Button
                  onClick={handleViewTranslations}
                  useTypography
                  size="sm"
                >
                  View
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Drawer>
      </ThemeProvider>
    </AppThemeContext.Provider>
  );
};

export default AppThemeProvider;
